// src/audit/index.js — екран „Одит": кой какво е променил (само админ).
//
// Данните идват от GET /api/audit (adminRequired на route-а; плочката е
// adminOnly). Тежката заявка върви в отделна нишка на сървъра.

import { el, clear, $, field, input } from '../shared/dom.js';
import { dateInput } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, periodGroup } from '../shared/ui.js';
import { whenFmt } from '../shared/audit.js';
import { state } from '../shared/state.js';

// Познатите раздели (table_name в журнала) с човешки имена.
const TABLES = [
  ['', 'Всички раздели'],
  ['contacts', 'Контакти'], ['invoices', 'Фактури'], ['proformas', 'Проформи'],
  ['protocols', 'Протоколи'], ['dividends', 'Дивиденти'], ['assemblies', 'Събрания'],
  ['tasks', 'Задачи'], ['checklist', 'Чек лист'], ['articles', 'Артикули'],
  ['companies', 'Мои фирми'], ['users', 'Потребители'],
  ['vehicles', 'Автопарк — коли'], ['vehicle_docs', 'Автопарк — документи'], ['vehicle_service', 'Автопарк — обслужване'],
  ['blanka_firms', 'Бланка — фирми'], ['blanka_entries', 'Бланка — клетки'], ['blanka_columns', 'Бланка — колони'],
  ['calendar_events', 'Календар — събития'], ['calendar_overrides', 'Календар — разместени дни'],
  ['vnoski', 'Протоколи — вноски'], ['waybills', 'Пътни листове'], ['vehicle_expenses', 'Автопарк — разходи'],
  ['dossiers', 'Досиета'], ['dossier_persons', 'Досиета — лица'], ['dossier_entries', 'Досиета — сметки'],
  ['email_log', 'Изпратени имейли'],
  ['fee_clients', 'Такси — клиенти'], ['fee_payments', 'Такси — плащания'], ['fee_adjustments', 'Такси — корекции'],
  ['fee_carryover', 'Такси — пренос'], ['fee_expenses', 'Такси — разходи'],
];
const TABLE_LABEL = Object.fromEntries(TABLES);

// (В.9) Обектът с ЧОВЕШКО име в единствено число: „Плащане (Такси) № 25",
// не „fee_payments #25". Непознато име минава през списъка на филтъра.
const OBJ_LABEL = {
  contacts: 'Клиент', invoices: 'Фактура', proformas: 'Проформа',
  protocols: 'Протокол (ВОП)', dividends: 'Дивидентен протокол', assemblies: 'Протокол ОС',
  vnoski: 'Протокол за вноски', tasks: 'Задача', checklist: 'Точка от чек листа',
  articles: 'Артикул', companies: 'Фирма', users: 'Потребител',
  vehicles: 'Автомобил', vehicle_docs: 'Документ (автопарк)', vehicle_service: 'Обслужване (автопарк)',
  vehicle_expenses: 'Разход (автопарк)', waybills: 'Пътен лист',
  blanka_firms: 'Фирма (бланка)', blanka_entries: 'Клетка (бланка)', blanka_columns: 'Колона (бланка)',
  dossiers: 'Досие', dossier_persons: 'Лице (досие)', dossier_entries: 'Сметка (досие)',
  calendar_events: 'Събитие в календара', calendar_overrides: 'Разместен ден',
  email_log: 'Изпратен имейл',
  fee_clients: 'Клиент (Такси)', fee_payments: 'Плащане (Такси)', fee_adjustments: 'Корекция (Такси)',
  fee_carryover: 'Пренос (Такси)', fee_expenses: 'Разход (Такси)',
};

const ACTION_LABEL = {
  create: ['Създаване', 'green'],
  update: ['Промяна', 'amber'],
  delete: ['Изтриване', 'red'],
  export: ['Износ (GDPR)', 'blue'],
  anonymize: ['Анонимизация', 'purple'],
};

// Кратка разлика „старо → ново": само променените полета, първите няколко.
// Служебните и обемните полета се пропускат, за да е четимо.
const SKIP_FIELDS = new Set(['history', 'items', 'updated_at', 'updated_by', 'updated_by_name', 'reminders_sent', 'installments', 'route', 'owners']);
function shortVal(v) {
  if (v == null || v === '') return '—';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 40 ? s.slice(0, 37) + '…' : s;
}
export function diffSummary(oldV, newV, action) {
  if (action === 'create') return newV && newV.name ? 'нов запис: ' + shortVal(newV.name) : (newV && newV.number ? 'нов запис № ' + shortVal(newV.number) : 'нов запис');
  if (action === 'delete') return oldV && oldV.name ? 'изтрит: ' + shortVal(oldV.name) : (oldV && oldV.number ? 'изтрит № ' + shortVal(oldV.number) : 'изтрит запис');
  const o = oldV || {}, n = newV || {};
  const changed = [];
  for (const k of new Set([...Object.keys(o), ...Object.keys(n)])) {
    if (SKIP_FIELDS.has(k)) continue;
    const a = o[k], b = n[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) changed.push(k + ': ' + shortVal(a) + ' → ' + shortVal(b));
  }
  if (!changed.length) return 'без видима разлика';
  const head = changed.slice(0, 3).join(' · ');
  return changed.length > 3 ? head + ' · и още ' + (changed.length - 3) : head;
}

export async function view(page) {
  clear(page);
  const st = state();
  if (!st.user || st.user.role !== 'admin') {
    page.appendChild(el('div', { class: 'panel' }, emptyState('≣', 'Одит журналът е достъпен само за администратор.')));
    return;
  }

  const f = {
    table: el('select', { 'aria-label': 'Раздел' }, ...TABLES.map(([v, t]) => el('option', { value: v, text: t }))),
    record: input({ type: 'number', min: '1', placeholder: '№ на запис', 'aria-label': '№ на запис', style: 'max-width:120px' }),
    user: el('select', { 'aria-label': 'Потребител' }, el('option', { value: '', text: 'Всички потребители' })),
    // Филтър по фирма (Промени 16.10, т. 6) — подредени по име, както в
    // превключвателя горе.
    company: el('select', { 'aria-label': 'Фирма' }, el('option', { value: '', text: 'Всички фирми' }),
      ...[...(st.companies || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'bg'))
        .map((c) => el('option', { value: c.id, text: c.name }))),
    from: dateInput(''),
    to: dateInput(''),
    limit: el('select', { 'aria-label': 'Колко реда да се покажат' },
      ...[50, 200, 500, 1000].map((n) => el('option', { value: n, text: n + ' реда', selected: n === 200 }))),
  };
  api('/auth/users').then((users) => {
    users.forEach((u) => f.user.appendChild(el('option', { value: u.id, text: u.name })));
  }).catch(() => {});

  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Одит — кой какво е променил' }),
    el('button', { class: 'btn sm', text: 'Приложи филтрите', onClick: () => load() })
  ));
  panel.appendChild(el('div', { class: 'filter-bar' },
    f.company, f.table, f.record, f.user,
    periodGroup(f.from, f.to),
    f.limit));

  const box = el('div', { style: 'overflow-x:auto' });
  panel.appendChild(box);
  page.appendChild(panel);

  const userName = {};
  const usersReady = api('/auth/users').then((users) => { users.forEach((u) => { userName[u.id] = u.name; }); }).catch(() => {});

  async function load() {
    clear(box);
    box.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));
    const params = new URLSearchParams();
    if (f.table.value) params.set('table', f.table.value);
    if (f.record.value) params.set('record_id', f.record.value);
    if (f.user.value) params.set('user_id', f.user.value);
    if (f.company.value) params.set('company_id', f.company.value);
    if (f.from.dataset.iso) params.set('from', f.from.dataset.iso);
    if (f.to.dataset.iso) params.set('to', f.to.dataset.iso);
    params.set('limit', f.limit.value);
    let rows = [];
    try { rows = await api('/audit?' + params.toString()); } catch (e) { clear(box); box.appendChild(el('div', { class: 'cal-hint', text: e.message })); return; }
    clear(box);
    if (!rows.length) { box.appendChild(emptyState('≣', 'Няма записи по тези филтри.')); return; }

    const tb = el('tbody');
    rows.forEach((r) => {
      const act = ACTION_LABEL[r.action] || [r.action, 'gray'];
      tb.appendChild(el('tr', {},
        el('td', { style: 'white-space:nowrap', text: whenFmt(r.ts) }),
        // Без потребител = фонова задача на сървъра (проверката във ВИЕС,
        // нощните задачи) — не човек (Макс, 30.10.2026: „защо не излиза кой е").
        el('td', { text: userName[r.user_id] || (r.user_id ? 'потребител #' + r.user_id : 'Системата (автоматично)') }),
        el('td', {}, el('span', { class: 'tag ' + act[1], text: act[0] })),
        el('td', { style: 'white-space:nowrap', text: (OBJ_LABEL[r.table_name] || TABLE_LABEL[r.table_name] || r.table_name) + ' № ' + r.record_id }),
        el('td', { style: 'font-size:13px;color:var(--muted)', text: diffSummary(r.old_values, r.new_values, r.action) })
      ));
    });
    box.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { text: 'Кога' }), el('th', { text: 'Кой' }), el('th', { text: 'Действие' }),
      el('th', { text: 'Обект' }), el('th', { text: 'Какво се промени' }))), tb));
    box.appendChild(el('div', { class: 'cal-hint', text: 'Показани ' + rows.length + ' реда (най-новите първи). Стеснете с филтрите при нужда.' }));
  }
  usersReady.then(load);
}
